import * as z from 'zod'

const ThemeColorsSchema = z.object({
  primary: z.string(),
  secondary: z.string(),
  accent: z.string(),
  error: z.string(),
  info: z.string(),
  success: z.string(),
  warning: z.string(),
  sidebar: z.string(),
  sidebar_secondary: z.string(),
})

const ApplicationThemeSchema = z.object({
  light: ThemeColorsSchema,
  dark: ThemeColorsSchema,
})
type ThemeColors = z.infer<typeof ThemeColorsSchema>
type ThemeColorsKey = keyof ThemeColors
type ApplicationTheme = z.infer<typeof ApplicationThemeSchema>
type ApplicationThemeKey = keyof ApplicationTheme

const ThemeSizeSchema = z.union([
  z.literal('EXTRA_SMALL'), z.literal('SMALL'), z.literal('NORMAL'), z.literal('LARGE'), z.literal('EXTRA_LARGE'),
])
export type ThemeSize = z.infer<typeof ThemeSizeSchema>

const ThemeFontSchema = z.union([
  z.literal('ROBOTO'), z.literal('OPEN_SANS'), z.literal('LATO'), z.literal('MONTSERRAT'), z.literal('OSWALD'),
  z.literal('SOURCE_SANS_PRO'), z.literal('SLABO_27PX'), z.literal('RALEWAY'), z.literal('PT_SANS'), z.literal('MERRIWEATHER'),
  z.literal('NUNITO_SANS'), z.literal('PROMPT'), z.literal('WORK_SANS'),
])
export type ThemeFont = z.infer<typeof ThemeFontSchema>

export const ThemeSettingsSchema = z.object({
  font: ThemeFontSchema.default('ROBOTO'),
  size: ThemeSizeSchema.default('NORMAL'),
})
// export type ThemeSettings = zod.infer<typeof ThemeSettingsSchema>

type SizeConfig = {
  headbarHeight: number
  fontSize: number
  tableRowHeight: number
  tableHeaderHeight: number
  listIndentSize: number
  listItemPaddingX: number
  listItemPaddingY: number
  listItemTwoLinePaddingX: number
  listItemTwoLinePaddingY: number
  listItemThreeLinePaddingX: number
  listItemThreeLinePaddingY: number
  listItemMinHeight: number
  listItemOneLineMinHeight: number
  listItemTwoLineMinHeight: number
  listItemThreeLineMinHeight: number
  fieldControlUnderlinedHeight: number
  buttonHeight: number
  datePickerMonthDaySize: number
  datePickerMonthBtnHeight: number
}
export const THEME_SIZES: Record<ThemeSize, SizeConfig> = {
  EXTRA_SMALL: {
    headbarHeight: 36,
    fontSize: 12,
    tableRowHeight: 32,
    tableHeaderHeight: 42,
    listIndentSize: 16,
    listItemPaddingX: 16,
    listItemPaddingY: 2,
    listItemTwoLinePaddingX: 16,
    listItemTwoLinePaddingY: 2,
    listItemThreeLinePaddingX: 16,
    listItemThreeLinePaddingY: 16,
    listItemMinHeight: 32,
    listItemOneLineMinHeight: 32,
    listItemTwoLineMinHeight: 32,
    listItemThreeLineMinHeight: 48,
    fieldControlUnderlinedHeight: 42,
    buttonHeight: 27,
    datePickerMonthDaySize: 30,
    datePickerMonthBtnHeight: 18,
  },
  SMALL: {
    headbarHeight: 40,
    fontSize: 14,
    tableRowHeight: 36,
    tableHeaderHeight: 46,
    listIndentSize: 16,
    listItemPaddingX: 16,
    listItemPaddingY: 2,
    listItemTwoLinePaddingX: 16,
    listItemTwoLinePaddingY: 2,
    listItemThreeLinePaddingX: 16,
    listItemThreeLinePaddingY: 16,
    listItemMinHeight: 36,
    listItemOneLineMinHeight: 36,
    listItemTwoLineMinHeight: 36,
    listItemThreeLineMinHeight: 56,
    fieldControlUnderlinedHeight: 45,
    buttonHeight: 32,
    datePickerMonthDaySize: 36,
    datePickerMonthBtnHeight: 24,
  },
  NORMAL: {
    headbarHeight: 52,
    fontSize: 16,
    tableRowHeight: 40,
    tableHeaderHeight: 50,
    listIndentSize: 16,
    listItemPaddingX: 16,
    listItemPaddingY: 2,
    listItemTwoLinePaddingX: 16,
    listItemTwoLinePaddingY: 2,
    listItemThreeLinePaddingX: 16,
    listItemThreeLinePaddingY: 16,
    listItemMinHeight: 40,
    listItemOneLineMinHeight: 40,
    listItemTwoLineMinHeight: 40,
    listItemThreeLineMinHeight: 64,
    fieldControlUnderlinedHeight: 40,
    buttonHeight: 36,
    datePickerMonthDaySize: 40,
    datePickerMonthBtnHeight: 24,
  },
  LARGE: {
    headbarHeight: 52,
    fontSize: 18,
    tableRowHeight: 48,
    tableHeaderHeight: 52,
    listIndentSize: 16,
    listItemPaddingX: 16,
    listItemPaddingY: 4,
    listItemTwoLinePaddingX: 16,
    listItemTwoLinePaddingY: 4,
    listItemThreeLinePaddingX: 16,
    listItemThreeLinePaddingY: 16,
    listItemMinHeight: 48,
    listItemOneLineMinHeight: 48,
    listItemTwoLineMinHeight: 48,
    listItemThreeLineMinHeight: 64,
    fieldControlUnderlinedHeight: 48,
    buttonHeight: 36,
    datePickerMonthDaySize: 40,
    datePickerMonthBtnHeight: 24,
  },
  EXTRA_LARGE: {
    headbarHeight: 52,
    fontSize: 20,
    tableRowHeight: 48,
    tableHeaderHeight: 52,
    listIndentSize: 16,
    listItemPaddingX: 16,
    listItemPaddingY: 4,
    listItemTwoLinePaddingX: 16,
    listItemTwoLinePaddingY: 4,
    listItemThreeLinePaddingX: 16,
    listItemThreeLinePaddingY: 16,
    listItemMinHeight: 48,
    listItemOneLineMinHeight: 48,
    listItemTwoLineMinHeight: 48,
    listItemThreeLineMinHeight: 64,
    fieldControlUnderlinedHeight: 48,
    buttonHeight: 36,
    datePickerMonthDaySize: 40,
    datePickerMonthBtnHeight: 24,
  },
}
type FontConfig = {
  fontFamily: string
  importRule: () => void
}
export const THEME_FONTS: Record<ThemeFont, FontConfig> = {
  ROBOTO: {
    fontFamily: '\'Roboto\', sans-serif;',
    importRule: () => import('@fontsource/roboto/500.css'),
  },
  OPEN_SANS: {
    fontFamily: '\'Open Sans\', sans-serif;',
    importRule: () => import('@fontsource/open-sans/500.css'),
  },
  LATO: {
    fontFamily: '\'Lato\', sans-serif;',
    importRule: () => import('@fontsource/lato/400.css'),
  },
  MONTSERRAT: {
    fontFamily: '\'Montserrat\', sans-serif;',
    importRule: () => import('@fontsource/montserrat/500.css'),
  },
  OSWALD: {
    fontFamily: '\'Oswald\', sans-serif;',
    importRule: () => import('@fontsource/oswald/400.css'),
  },
  SOURCE_SANS_PRO: {
    fontFamily: '\'Source Sans Pro\', sans-serif;',
    importRule: () => import('@fontsource/source-sans-pro/600.css'),
  },
  SLABO_27PX: {
    fontFamily: '\'Slabo 27px\', sans-serif;',
    importRule: () => import('@fontsource/slabo-27px/400.css'),
  },
  RALEWAY: {
    fontFamily: '\'Raleway\', sans-serif;',
    importRule: () => import('@fontsource/raleway/500.css'),
  },
  PT_SANS: {
    fontFamily: '\'PT Sans\', sans-serif;',
    importRule: () => import('@fontsource/pt-sans/400.css'),
  },
  MERRIWEATHER: {
    fontFamily: '\'Merriweather\', sans-serif;',
    importRule: () => import('@fontsource/merriweather/400.css'),
  },
  NUNITO_SANS: {
    fontFamily: '\'Nunito Sans\', sans-serif;',
    importRule: () => import('@fontsource/nunito-sans/400.css'),
  },
  PROMPT: {
    fontFamily: '\'Prompt\', sans-serif;',
    importRule: () => import('@fontsource/prompt/400.css'),
  },
  WORK_SANS: {
    fontFamily: '\'Work Sans\', sans-serif;',
    importRule: () => import('@fontsource/work-sans/500.css'),
  },
}
export {
  ApplicationThemeSchema
}
export type {
  ApplicationTheme,
  ApplicationThemeKey,
  ThemeColorsKey
}
